import { Accordion, AccordionContent, AccordionTitle, Button, Checkbox, Dropdown, FormCheckbox, Icon, Input } from "semantic-ui-react";
import CustomModal from "src/components/configuracion/components/customModal";
import './CashFlowHolding.scss'
import { useState } from "react";
import { MergeCategoryAreas, UnmergeArea, UpdateMergedArea } from "src/graphql/cashflowHolding";
import { useDispatch } from "react-redux";
import { importAlertState } from "src/redux/actions/alert";
import { useEffect } from "react";
import { useMemo } from "react";

export const MergeAreasModal = ({
    modalIsOpen,
    incomeList,
    expenseList,
    holdingCashFlowId,
    setMergeAreasModalIsOpen,
    reexecuteHoldingFlow,
    reExecuteQueryHoldingFlowStructure,
    mergeAreasModalData,
    setMergeAreasModalData
}) => {
    const dispatch = useDispatch();

    // *********************** Variables *********************** //
    const [hasInitialized, setHasInitialized] = useState(false);
    const [mergedAreaName, setMergedAreaName] = useState('');
    const [incomeActiveIndices, setIncomeActiveIndices] = useState([]);
    const [expenseActiveIndices, setExpenseActiveIndices] = useState([]);
    const [selectedAreaSelections, setSelectedAreaSelections] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState();
    const [dropdownOptions, setDropdownOptions] = useState([]);
    const [targetCategory, setTargetCategory] = useState();
    const [cleanedIncomeList, setCleanedIncomeList] = useState([]);
    const [cleanedExpenseList, setCleanedExpenseList] = useState([]);
    const [selectionError, setSelectionError] = useState('');

    // *********************** Graph *************************** //
    const { fetching, executeMutation: mergeCategoryAreasExecuteMutation } = MergeCategoryAreas();
    const { fetching: unmergeAreaFetching, executeMutation: unmergeAreaExecuteMutation } = UnmergeArea();
    const { fetching: UpdateMergedAreaFetching, executeMutation: updateMergedAreaExecuteMutation } = UpdateMergedArea();

    // *********************** Lógica *************************** //
    const normalizeLabel = (label) => (label || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
    const getMergeRef = (field, category) => `${field?.cashflowAreaId}::${category?.cashflowCategoryId}::${normalizeLabel(field?.label)}`;
    const isFieldIncludedInMergedRefs = (field, category, mergedRefs = []) => {
        const mergeRef = getMergeRef(field, category);
        return mergedRefs?.includes(mergeRef) || mergedRefs?.includes(field?.cashflowAreaId);
    };

    const getSelectionData = (field, category, type, index) => ({
        key: `${type}-${category?.cashflowCategoryId}-${field?.cashflowAreaId}-${field?.label}-${index}`,
        areaId: field?.cashflowAreaId,
        categoryId: category?.cashflowCategoryId,
        mergeRef: getMergeRef(field, category),
    });

    const selectedAreaIds = useMemo(
        () => selectedAreaSelections.map((selection) => selection.mergeRef).filter(Boolean),
        [selectedAreaSelections]
    );
    const selectedCategoryIds = useMemo(
        () => [...new Set(selectedAreaSelections.map((selection) => String(selection.categoryId)).filter(Boolean))],
        [selectedAreaSelections]
    );
    const hasMixedCategories = selectedCategoryIds.length > 1;
    const targetCategoryName = dropdownOptions.find((option) => String(option.value) === String(targetCategory))?.text || 'Sin categoría';
    const isFieldAlreadyMerged = (field) => {
        const hasMergedFromData = Array.isArray(field?.mergedFrom) ? field.mergedFrom.length > 0 : !!field?.mergedFrom;
        return !!field?.isMerged || hasMergedFromData;
    };

    const handleClick = (index, type) => {
        if (type === 'income') {
            setIncomeActiveIndices(prev =>
                prev.includes(index)
                    ? prev.filter(i => i !== index)
                    : [...prev, index]
            );
        }
        if (type === 'expense') {
            setExpenseActiveIndices(prev =>
                prev.includes(index)
                    ? prev.filter(i => i !== index)
                    : [...prev, index]
            );
        }
    };

    useEffect(() => {
        if (!modalIsOpen || hasInitialized) return;

        let categoryIdSet = false;

        const activeIncomeIndices = incomeList.reduce((acc, income, index) => {
            const hasSelectedField = income.fields.some(field =>
                selectedAreaSelections.some((selection) => selection.mergeRef === getMergeRef(field, income))
            );
            if (hasSelectedField) {
                acc.push(index);
                if (!categoryIdSet) {
                    setSelectedCategoryId(income.cashflowCategoryId);
                    categoryIdSet = true;
                }
            }
            return acc;
        }, []);

        const activeExpenseIndices = expenseList.reduce((acc, expense, index) => {
            const hasSelectedField = expense.fields.some(field =>
                selectedAreaSelections.some((selection) => selection.mergeRef === getMergeRef(field, expense))
            );
            if (hasSelectedField) {
                acc.push(index);
                if (!categoryIdSet) {
                    setSelectedCategoryId(expense.cashflowCategoryId);
                    categoryIdSet = true;
                }
            }
            return acc;
        }, []);

        setIncomeActiveIndices(activeIncomeIndices);
        setExpenseActiveIndices(activeExpenseIndices);
        getDropdownOptions();
        cleanAreaOptions();
        setHasInitialized(true);
    }, [modalIsOpen, incomeList, expenseList, selectedAreaSelections]);

    useEffect(() => {
        if (!modalIsOpen || !mergeAreasModalData?.mergedFrom?.length || hasInitialized) return;

        const preselected = [];
        const includeIfMerged = (field, category, type, index) => {
            if (isFieldIncludedInMergedRefs(field, category, mergeAreasModalData.mergedFrom)) {
                preselected.push(getSelectionData(field, category, type, index));
            }
        };

        incomeList?.forEach((income) => {
            income?.fields?.forEach((field, index) => includeIfMerged(field, income, "income", index));
        });
        expenseList?.forEach((expense) => {
            expense?.fields?.forEach((field, index) => includeIfMerged(field, expense, "expense", index));
        });

        setSelectedAreaSelections(preselected);
    }, [modalIsOpen, mergeAreasModalData, incomeList, expenseList, hasInitialized]);

    const onCheckboxChange = (isChecked, selectionData, category, field) => {
        setSelectionError('');
        if (!selectedCategoryId) {
            setSelectedCategoryId(category.cashflowCategoryId);
        }

        if (isChecked) {
            const effectiveTargetCategory = targetCategory || category.cashflowCategoryId;
            if (!targetCategory) setTargetCategory(category.cashflowCategoryId);
            if (String(effectiveTargetCategory) !== String(category.cashflowCategoryId)) {
                setSelectionError('Debes combinar dentro de una misma categoría');
                return;
            }
            if (isFieldAlreadyMerged(field) && !isFieldIncludedInMergedRefs(field, category, mergeAreasModalData?.mergedFrom)) {
                setSelectionError('Esta área ya está combinada. Debes separarla antes de volver a combinar.');
                return;
            }
            setSelectedAreaSelections((prev) => {
                if (prev.some((selection) => selection.key === selectionData.key)) return prev;
                return [...prev, selectionData];
            });
        } else {
            setSelectedAreaSelections((prev) =>
                prev.filter((selection) => selection.key !== selectionData.key)
            );
        }
    }

    useEffect(() => {
        if (!targetCategory) return;
        setSelectionError('');
        setSelectedAreaSelections((prev) =>
            prev.filter((selection) => String(selection.categoryId) === String(targetCategory))
        );
    }, [targetCategory]);

    const mergeAreas = async () => {
        dispatch(
            importAlertState({
                content: "Creando nueva área...",
                state: "loading",
                duration: 10
            })
        )

        const { data, error } = await mergeCategoryAreasExecuteMutation(
            {
                mergedAreaName: mergedAreaName,
                targetCategoryId: targetCategory,
                areaIdsToMerge: selectedAreaIds,
                holdingFlowId: holdingCashFlowId,
            }
        );

        if (data?.mergeCategoryAreas) {
            dispatch(
                importAlertState({
                    content: data?.mergeCategoryAreas?.message,
                    state: data?.mergeCategoryAreas?.status,
                    duration: 5
                })
            );
        }

        if (error) {
            dispatch(
                importAlertState({
                    content: "Error al combinar área.",
                    state: "success",
                    duration: 5
                })
            );
        }

        if (data?.mergeCategoryAreas?.status == 'success') {
            onClose();
        }
    }

    const unmergeAreas = async () => {
        dispatch(
            importAlertState({
                content: "Separando área...",
                state: "loading",
                duration: 10
            })
        )

        const { data, error } = await unmergeAreaExecuteMutation(
            {
                holdingFlowId: holdingCashFlowId,
                mergedAreaId: mergeAreasModalData?.cashflowAreaId,
            }
        );

        if (data?.unmergeArea) {
            dispatch(
                importAlertState({
                    content: data?.unmergeArea?.message,
                    state: data?.unmergeArea?.status,
                    duration: 5
                })
            );
        }

        if (error) {
            dispatch(
                importAlertState({
                    content: "Error al separar área.",
                    state: "success",
                    duration: 5
                })
            );
        }

        if (data?.unmergeArea?.status == 'success') {
            onClose();
        }
    }

    const updateMergedAreas = async () => {
        dispatch(
            importAlertState({
                content: "Editando área...",
                state: "loading",
                duration: 10
            })
        )

        const { data, error } = await updateMergedAreaExecuteMutation(
            {
                holdingFlowId: holdingCashFlowId,
                mergedAreaId: mergeAreasModalData?.cashflowAreaId,
                targetCategoryId: targetCategory,
                newName: mergedAreaName,
                newAreaIds: selectedAreaIds
            }
        );

        if (data?.updateMergedArea) {
            dispatch(
                importAlertState({
                    content: data?.updateMergedArea?.message,
                    state: data?.updateMergedArea?.status,
                    duration: 5
                })
            );
        }

        if (error) {
            dispatch(
                importAlertState({
                    content: "Error al editar área.",
                    state: "success",
                    duration: 5
                })
            );
        }

        if (data?.updateMergedArea?.status == 'success') {
            onClose();
        }
    }

    const onClose = () => {
        setMergeAreasModalIsOpen(undefined);
        setIncomeActiveIndices([]);
        setSelectedAreaSelections([]);
        setSelectedCategoryId(undefined);
        setMergeAreasModalIsOpen(false);
        setMergedAreaName('');
        reexecuteHoldingFlow({ requestPolicy: 'cache-and-network' });
        reExecuteQueryHoldingFlowStructure({ requestPolicy: 'cache-and-network' });
        setTargetCategory('');
        setSelectionError('');
        setHasInitialized(false);
        if (mergeAreasModalData) {
            setMergeAreasModalData(undefined);
        }
    }

    useEffect(() => {
        if (!mergeAreasModalData) return;
        setMergedAreaName(mergeAreasModalData?.label);
        setSelectedAreaSelections([]);
        setTargetCategory(mergeAreasModalData?.cashflowCategoryId);
    }, [mergeAreasModalData])

    const getDropdownOptions = () => {
        let options = [];
        incomeList?.map((income) => {
            const option = {
                key: income?.cashflowCategoryId,
                text: income?.label,
                value: income?.cashflowCategoryId
            };

            options.push(option);
        });

        expenseList?.map((expense) => {
            const option = {
                key: expense?.cashflowCategoryId,
                text: expense?.label,
                value: expense?.cashflowCategoryId
            };

            options.push(option);
        });

        setDropdownOptions(options);
    }

    const cleanAreaOptions = () => {
        const cleanedIncomes = incomeList.map((income) => ({
            ...income,
            fields: income?.fields?.filter(field => {
                if (isFieldIncludedInMergedRefs(field, income, mergeAreasModalData?.mergedFrom)) return true;
                if (mergeAreasModalData) {
                    const hasMergedFromData = Array.isArray(field.mergedFrom)
                        ? field.mergedFrom.length > 0
                        : !!field.mergedFrom;
                    return !hasMergedFromData && !field.isMerged;
                }
                return true;
            }) ?? []
        }));

        setCleanedIncomeList(cleanedIncomes);

        const cleanedExpenses = expenseList.map((expense) => ({
            ...expense,
            fields: expense?.fields?.filter(field => {
                if (isFieldIncludedInMergedRefs(field, expense, mergeAreasModalData?.mergedFrom)) return true;
                if (mergeAreasModalData) {
                    const hasMergedFromData = Array.isArray(field.mergedFrom)
                        ? field.mergedFrom.length > 0
                        : !!field.mergedFrom;
                    return !hasMergedFromData && !field.isMerged;
                }
                return true;
            }) ?? []
        }));

        setCleanedExpenseList(cleanedExpenses);
    }

    return (
        <CustomModal
            open={modalIsOpen}
            onClose={onClose}
            title={"Combinar Áreas - Flujo de caja"}
            classNameTitle={"tw-text-center"}
            size="large"
            classNameModal="merge-areas-modal"
            classNameContent="merge-areas-modal-content-wrapper"
            modalStyle={{ width: "min(1240px, 98vw)" }}
            content={
                <div className="tw-flex tw-flex-col tw-gap-[20px]">
                    <span className="tw-font-light tw-text-sm">
                        Selecciona dos o más áreas, ingresa un nombre y haz clic en "Combinar". El monto total se actualizará en el flujo de caja.
                    </span>

                    <section>
                        <div className="tw-flex tw-justify-between tw-flex-wrap tw-gap-3 cash-flow-filters">
                            <div className="tw-flex tw-gap-3 tw-w-full tw-flex-wrap">
                                <Input
                                    placeholder="Nombre de la nueva área combinada"
                                    className="tw-w-[340px] !tw-h-full !tw-text-base"
                                    onChange={(event) => setMergedAreaName(event.target.value)}
                                    value={mergedAreaName}
                                />

                                <div className="tw-w-[240px]">
                                    <Dropdown
                                        placeholder='Seleccionar Categoría'
                                        fluid
                                        selection
                                        className="tw-h-full"
                                        options={dropdownOptions}
                                        value={targetCategory}
                                        onChange={(event, data) => setTargetCategory(data.value)}
                                    />
                                </div>

                                <Button
                                    className="merge-areas-modal-action-btn !tw-h-full !tw-w-[120px] !tw-font-medium !tw-text-sm"
                                    onClick={
                                        mergeAreasModalData
                                            ? updateMergedAreas
                                            : mergeAreas
                                    }
                                    disabled={selectedAreaSelections.length < 2 || !mergedAreaName || !targetCategory || hasMixedCategories || fetching || unmergeAreaFetching}
                                >
                                    {
                                        mergeAreasModalData
                                            ? 'Editar'
                                            : 'Combinar'
                                    }
                                </Button>
                                {
                                    mergeAreasModalData?.cashflowAreaId &&
                                    <Button
                                        className="merge-areas-modal-action-btn !tw-h-full !tw-w-[120px] !tw-font-medium !tw-text-sm"
                                        onClick={unmergeAreas}
                                        disabled={fetching || unmergeAreaFetching}
                                    >
                                        Separar
                                    </Button>
                                }
                            </div>

                            {/* <div className="tw-w-[195px] tw-h-full tw-flex tw-items-center tw-justify-between tw-gap-[14px]">
                                <Checkbox
                                    toggle
                                    className="custom-checkbox"
                                />

                                <span className="tw-font-light tw-text-sm">
                                    Mostrar todas las áreas
                                </span>
                            </div> */}
                        </div>
                        {(selectionError || hasMixedCategories) && (
                            <div className="tw-mt-2 tw-text-sm tw-text-red-600">
                                {selectionError || 'Debes combinar dentro de una misma categoría'}
                            </div>
                        )}

                    </section>

                    <section className="tw-rounded tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-3 tw-py-2">
                        <div className="tw-text-sm tw-text-slate-700">
                            Combinarás: <span className="tw-font-semibold">{selectedAreaSelections.length}</span> áreas
                        </div>
                        <div className="tw-text-sm tw-text-slate-700">
                            Resultado: Área <span className="tw-font-semibold">{mergedAreaName || 'Sin nombre'}</span> en categoría <span className="tw-font-semibold">{targetCategoryName}</span>
                        </div>
                    </section>


                    <section className="tw-flex tw-gap-5 tw-justify-between merge-areas-modal-columns">
                        <div className="tw-flex-1 tw-rounded-[5px] tw-min-h-[419px] tw-border-solid tw-border tw-border-gray-200 merge-areas-modal-column">
                            <div className="tw-h-[37px] tw-w-full tw-py-[8px] tw-px-[15px] tw-bg-gray-100 tw-rounded-t-[5px]">
                                <span className="tw-font-medium tw-text-base">INGRESOS</span>
                            </div>
                            <div className="tw-h-full tw-w-full tw-py-[5px] tw-px-[10px] merge-areas-modal-column-body">
                                <Accordion exclusive={false}>
                                    {
                                        cleanedIncomeList?.map((income, index) => {
                                            return (
                                                income?.fields.length > 0 &&
                                                <>
                                                    <AccordionTitle
                                                        key={`title-${index}`}
                                                        active={incomeActiveIndices.includes(index)}
                                                        index={index}
                                                        onClick={() => handleClick(index, 'income')}
                                                    >
                                                        <Icon name={incomeActiveIndices.includes(index) ? 'angle down' : 'angle right'} />
                                                        <span className="tw-font-normal tw-text-sm">
                                                            {income?.label}
                                                        </span>
                                                    </AccordionTitle>
                                                    <AccordionContent key={`content-${index}`} active={incomeActiveIndices.includes(index)}>
                                                        {
                                                            income?.fields.map((field, fieldIndex) => {
                                                                const selectionData = getSelectionData(field, income, 'income', fieldIndex);
                                                                const isAlreadyMerged = isFieldAlreadyMerged(field) && !isFieldIncludedInMergedRefs(field, income, mergeAreasModalData?.mergedFrom);
                                                                const isChecked = selectedAreaSelections.some((selection) => selection.key === selectionData.key);
                                                                const isDisabled = (targetCategory && String(targetCategory) !== String(income?.cashflowCategoryId) && !isChecked) || isAlreadyMerged;
                                                                return (
                                                                    <div
                                                                        key={selectionData.key}
                                                                        className="tw-py-[5px] tw-px-[15px] tw-h-[28px] tw-flex tw-gap-[22px] tw-items-center"
                                                                    >
                                                                        <FormCheckbox
                                                                            id={`merge-${selectionData.key}`}
                                                                            onChange={(e, data) => onCheckboxChange(data.checked, selectionData, income, field)}
                                                                            checked={isChecked}
                                                                            disabled={isDisabled}
                                                                        />
                                                                        <span className="tw-font-light tw-text-sm tw-break-words">
                                                                            {field?.label}
                                                                        </span>
                                                                        {isAlreadyMerged && (
                                                                            <span className="tw-text-[10px] tw-font-medium tw-bg-slate-200 tw-text-slate-600 tw-px-2 tw-py-[2px] tw-rounded">
                                                                                Combinada
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )
                                                            })
                                                        }
                                                    </AccordionContent>
                                                </>
                                            )
                                        })
                                    }
                                </Accordion>
                            </div>
                        </div>
                        <div className="tw-flex-1 tw-rounded-[5px] tw-min-h-[419px] tw-border-solid tw-border tw-border-gray-200 merge-areas-modal-column">
                            <div className="tw-h-[37px] tw-w-full tw-py-[8px] tw-px-[15px] tw-bg-gray-100 tw-rounded-t-[5px]">
                                <span className="tw-font-medium tw-text-base">EGRESOS</span>
                            </div>
                            <div className="tw-h-full tw-w-full tw-py-[5px] tw-px-[10px] merge-areas-modal-column-body">
                                <Accordion exclusive={false}>
                                    {
                                        cleanedExpenseList?.map((expense, index) => {
                                            return (
                                                <>
                                                    <AccordionTitle
                                                        key={`title-${index}`}
                                                        active={expenseActiveIndices.includes(index)}
                                                        index={index}
                                                        onClick={() => handleClick(index, 'expense')}
                                                    >
                                                        <Icon name={expenseActiveIndices.includes(index) ? 'angle down' : 'angle right'} />
                                                        <span className="tw-font-normal tw-text-sm">
                                                            {expense?.label}
                                                        </span>
                                                    </AccordionTitle>
                                                    <AccordionContent key={`content-${index}`} active={expenseActiveIndices.includes(index)}>
                                                        {
                                                            expense?.fields.map((field, fieldIndex) => {
                                                                const selectionData = getSelectionData(field, expense, 'expense', fieldIndex);
                                                                const isAlreadyMerged = isFieldAlreadyMerged(field) && !isFieldIncludedInMergedRefs(field, expense, mergeAreasModalData?.mergedFrom);
                                                                const isChecked = selectedAreaSelections.some((selection) => selection.key === selectionData.key);
                                                                const isDisabled = (targetCategory && String(targetCategory) !== String(expense?.cashflowCategoryId) && !isChecked) || isAlreadyMerged;
                                                                return (
                                                                    <div
                                                                        key={selectionData.key}
                                                                        className="tw-py-[5px] tw-px-[15px] tw-h-[28px] tw-flex tw-gap-[22px] tw-items-center"
                                                                    >
                                                                        <FormCheckbox
                                                                            id={`merge-${selectionData.key}`}
                                                                            onChange={(e, data) => onCheckboxChange(data.checked, selectionData, expense, field)}
                                                                            checked={isChecked}
                                                                            disabled={isDisabled}
                                                                        />
                                                                        <span className="tw-font-light tw-text-sm tw-break-words">
                                                                            {field?.label}
                                                                        </span>
                                                                        {isAlreadyMerged && (
                                                                            <span className="tw-text-[10px] tw-font-medium tw-bg-slate-200 tw-text-slate-600 tw-px-2 tw-py-[2px] tw-rounded">
                                                                                Combinada
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )
                                                            })
                                                        }
                                                    </AccordionContent>
                                                </>
                                            )
                                        })
                                    }
                                </Accordion>
                            </div>
                        </div>
                    </section>
                </div>
            }
        />
    );
};
